import React from "react";
import { ArrowUpRight, Dumbbell, UtensilsCrossed, Briefcase, Cpu } from "lucide-react";
import { SectionHeader } from "@/sections/Services";

const CASES = [
  {
    tag: "Sector · Fitness",
    icon: Dumbbell,
    title: "Fitness Business Website",
    copy: "Modern online presence for a gym/fitness brand, focused on credibility, enquiries and member engagement.",
    metric: "Enquiries lifted across digital channels",
    outcome: "Improved customer engagement and stronger online credibility.",
    accent: "from-[#0A66FF]/30 via-transparent to-[#00D4FF]/10",
  },
  {
    tag: "Sector · Hospitality",
    icon: UtensilsCrossed,
    title: "Hospitality Website",
    copy: "Digital presence for a café/restaurant-style business, focused on presentation, menus, visibility and customer trust.",
    metric: "Customer-facing UX redesign",
    outcome: "Stronger brand presentation and clearer customer journey.",
    accent: "from-[#00D4FF]/25 via-transparent to-[#0A66FF]/15",
  },
  {
    tag: "Sector · Professional Services",
    icon: Briefcase,
    title: "Professional Services Platform",
    copy: "Website and digital workflow support for accountants, law firms and service-led businesses.",
    metric: "Client portal & workflow ready",
    outcome: "Streamlined client communication and a more professional digital footprint.",
    accent: "from-[#0A66FF]/30 via-transparent to-transparent",
  },
  {
    tag: "Sector · Software & AI",
    icon: Cpu,
    title: "App & Product Concepts",
    copy: "Rapid prototyping and build support for new software products, mobile apps and AI-assisted digital platforms.",
    metric: "From concept to prototype quickly",
    outcome: "Validated concepts moved from idea to functional prototype.",
    accent: "from-[#00D4FF]/30 via-transparent to-[#0A66FF]/10",
  },
];

export default function Work() {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-28 md:py-36 border-t border-white/[0.06]"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
        <div className="mk-reveal mb-3">
          <span className="text-[11px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            Practical solutions delivered across multiple sectors
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <SectionHeader
            eyebrow="Work"
            title="Experience across multiple sectors"
            subtitle="Real client names anonymised. Real outcomes, real industries — from local SMEs to growing service businesses across the UK."
          />
          <a
            href="#contact"
            data-testid="work-discuss-link"
            className="mk-reveal inline-flex shrink-0 items-center gap-1.5 mk-btn-secondary px-5 py-3 rounded-full text-sm font-semibold"
          >
            Discuss a similar project <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
          {CASES.map((c, i) => (
            <CaseCard key={c.title} index={i} {...c} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseCard({ icon: Icon, tag, title, copy, metric, outcome, accent, index }) {
  return (
    <article
      data-testid={`case-card-${index}`}
      className="mk-reveal mk-card mk-glow group rounded-2xl overflow-hidden flex flex-col"
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      <div className={`relative h-52 md:h-64 bg-gradient-to-br ${accent} border-b border-white/10 overflow-hidden`}>
        <div className="absolute inset-0 mk-grid-bg opacity-40" aria-hidden />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative inline-flex h-20 w-20 items-center justify-center rounded-2xl border border-white/15 bg-[#07111F]/60 backdrop-blur-md">
            <Icon className="h-8 w-8 text-[#00D4FF]" />
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-[#00D4FF] mk-pulse" />
          </div>
        </div>
        <div className="absolute top-4 left-4 inline-flex items-center rounded-full border border-white/10 bg-[#07111F]/70 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-slate-300">
          {tag}
        </div>
      </div>

      <div className="p-8 md:p-10 flex-1 flex flex-col">
        <h3 className="font-display text-2xl md:text-[26px] tracking-tight text-white">
          {title}
        </h3>
        <p className="mt-3 text-[15px] text-slate-400 leading-relaxed">{copy}</p>

        <div
          className="mt-7 rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4"
          data-testid={`case-outcome-${index}`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#00D4FF] font-medium">
            Outcome
          </div>
          <p className="mt-1.5 text-[14px] text-slate-300 leading-relaxed">
            {outcome}
          </p>
        </div>

        <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-xs text-slate-500">{metric}</span>
          <ArrowUpRight className="h-4 w-4 text-slate-500 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#00D4FF]" />
        </div>
      </div>
    </article>
  );
}
